// src/components/store/sections/gallery/DynamicGalleryVariant.tsx
"use client";

import React, { useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Images, Sparkles } from "lucide-react";

const FALLBACK_GALLERY = [
  "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=600&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1503602642458-232111445657?w=600&auto=format&fit=crop&q=80",
];

export default function DynamicGalleryVariant({
  variantNumber,
  archKey,
  archLabel,
  palName,
  grad,
  lightBg,
  textCol,
  borderCol,
  initialImages = [],
}: any) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rawList = Array.isArray(initialImages) && initialImages.length > 0 ? initialImages : FALLBACK_GALLERY;

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 select-none font-sans space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-xs font-black uppercase tracking-wider flex items-center gap-1.5 font-mono ${textCol}`}>
            <Images className="w-3.5 h-3.5" /> Variant {variantNumber}: {archLabel} ({palName})
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mt-0.5">
            Store Lookbook & Media Gallery
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scroll("left")}
            className={`w-8 h-8 rounded-full border-2 ${borderCol} bg-white text-slate-700 flex items-center justify-center shadow-xs cursor-pointer active:scale-95`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scroll("right")}
            className={`w-8 h-8 rounded-full border-2 ${borderCol} bg-white text-slate-700 flex items-center justify-center shadow-xs cursor-pointer active:scale-95`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex items-stretch gap-4 overflow-x-auto pb-4 pt-2 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {rawList.map((item: any, idx: number) => {
          const rawUrl = typeof item === "string" ? item : item?.image || FALLBACK_GALLERY[idx % FALLBACK_GALLERY.length];
          const src = rawUrl.startsWith("http") ? rawUrl : FALLBACK_GALLERY[idx % FALLBACK_GALLERY.length];
          const shapeCls =
            archKey === "cathedral_windows"
              ? "rounded-t-[90px] rounded-b-3xl"
              : archKey === "capsule_portals"
              ? "rounded-[999px]"
              : archKey === "polaroid_wall"
              ? "rounded-2xl p-2.5 pb-7 bg-white shadow-lg"
              : "rounded-3xl";

          return (
            <div
              key={idx}
              className={`w-48 sm:w-56 h-60 shrink-0 relative overflow-hidden border-2 ${borderCol} bg-gradient-to-br ${grad} p-1 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 group ${shapeCls}`}
            >
              <div className={`relative w-full h-full overflow-hidden bg-white ${shapeCls}`}>
                <Image src={src} alt={`Lookbook ${idx + 1}`} fill unoptimized className="object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute bottom-2.5 left-2.5 right-2.5 px-3 py-1.5 rounded-xl bg-black/60 backdrop-blur-md text-white text-[10px] font-mono font-bold flex items-center justify-between">
                  <span>LOOK #{idx + 1}</span>
                  <Sparkles className="w-3 h-3 text-amber-300" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
